/**
 * Home page (landing inside the app).
 */
import { ArrowBackIosNew, ArrowForwardIos, Description, HelpOutline, History, PlayArrow } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type QuickLink = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
};

const quickLinks: QuickLink[] = [
  {
    id: 'history',
    title: 'History',
    description: 'Explore history, deposits, layers, and wells.',
    icon: <History />,
    to: '/app/history',
  },
  {
    id: 'templates',
    title: 'Templates',
    description: 'Manage reservoir templates and defaults.',
    icon: <Description />,
    to: '/app/templates',
  },
  {
    id: 'scenarios',
    title: 'Scenarios',
    description: 'Create scenarios and run calculations.',
    icon: <PlayArrow />,
    to: '/app/scenarios',
  },
];

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tourOpen, setTourOpen] = useState(false);
  const [tourSlide, setTourSlide] = useState(0);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const theme = useTheme();
  const dialogPaperRef = useRef<HTMLDivElement | null>(null);

  const slides = useMemo(
    () => [
      {
        id: 'General',
        tabLabel: 'General',
        title: 'General',
        body: `QuickField is a rapid greenfield evaluation tool that pairs smart machine-learning models with advanced reservoir-engineering calculations to deliver P10, P50, and P90 production forecasts in minutes. Users can manage their private history data for private and company fields and have an access to public data. Click "right" button to see main features of the app and workflow steps. `,
        images: []
      },
      {
        id: 'history',
        tabLabel: 'Historical data',
        title: '1. Historical data',
        body: `In this section, you can access historical data for your fields, company-shared public data, and open datasets prepared for you. Each field includes reservoirs with petrophysical properties, production history, and well data associated with those reservoirs.`,
        images: [
          { src: '/page-help/history.png', alt: 'Fields and datasets list' },
        ],
      },
      {
        id: 'templates',
        tabLabel: 'Templates',
        title: '2. Templates',
        body: `Templates are presets you can use in scenario calculations. Once a template is created, you can load it into a scenario and adjust it within the scenario—while the original template remains unchanged. Use public templates, create your own, and share them with your colleagues. Users may:`,
        steps: [
          'View public, private, and company-public templates.',
          'Create your own templates and share them with your colleagues.',
          'Edit your own templates and company-public templates.'
        ],
        images: [
          { src: '/page-help/templates_1.png', alt: 'Reservoir templates list' },
          { src: '/page-help/templates_2.png', alt: 'Reservoir template detail' }
        ],
      },
      {
        id: 'scenarios',
        tabLabel: 'Scenarios',
        title: '3. Scenarios',
        body: `A calculated scenario is a set of reservoirs with filled petrophysical properties, wells linked to these reservoirs, and simulation results. You can view public scenarios, create your own and company-public scenarios, edit them and export for integration with other software`,
        steps: [
          'Create an empty scenario in the Scenarios section.',
          'Set general information.',
          'Add reservoirs from templates or create new ones.',
          'Add wells and link them to reservoirs.',
          'Run calculations and view results.',
          'Export scenario for integration with other software.'
        ],
        images: [
          { src: '/page-help/scenarios_list.png', alt: 'Scenarios list' }
        ],
      },
      {
        id: 'scenarios_1',
        tabLabel: 'Scenarios',
        title: '4. Create Scenarios. General.',
        body: `Start creating a scenario by clicking the "Add Scenario" button. Fill in the general information, including the scenario name, description and visibility.`,
        images: [
          { src: '/page-help/scenarios_create_1.png', alt: 'Create scenario' }
        ],
      },
      {
        id: 'scenarios_2',
        tabLabel: 'Scenarios',
        title: '5. Create Scenarios. Reservoirs.',
        body: `Add reservoirs to the scenario by clicking the "Add Reservoir" button. You can choose to create a new reservoir or use a template.`,
        images: [
          { src: '/page-help/scenarios_create_2.png', alt: 'Create scenario' },
          { src: '/page-help/scenarios_create_3.png', alt: 'Create scenario' }
        ],
      },
      {
        id: 'scenarios_3',
        tabLabel: 'Scenarios',
        title: '5. Create Scenarios. Wells.',
        body: `Add wells to the scenario by clicking the "Add Well" button. Bind each well to a reservoir and set the entry date`,
        images: [
          { src: '/page-help/scenarios_create_4.png', alt: 'Create scenario' }
        ],
      },
      {
        id: 'scenarios_4',
        tabLabel: 'Scenarios',
        title: '6. Create Scenarios. Results.',
        body: `Set the Monte-Carlo simulations parameters and run the calculation. The results will contain production forecasts, decline curves, sensitivity analysis.`,
        images: [
          { src: '/page-help/scenarios_create_5.png', alt: 'Create scenario' },
          { src: '/page-help/scenarios_create_6.png', alt: 'Create scenario' },
          { src: '/page-help/scenarios_create_7.png', alt: 'Create scenario' }
        ],
      },
    ],
    []
  );

  const activeSlide = slides[Math.min(slides.length - 1, Math.max(0, tourSlide))];

  const handlePrev = () => setTourSlide((s) => Math.max(0, s - 1));
  const handleNext = () => setTourSlide((s) => Math.min(slides.length - 1, s + 1));
  const closeLightbox = () => setLightbox(null);

  useEffect(() => {
    if (!lightbox) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [lightbox]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tour') === '1') {
      setTourSlide(0);
      setTourOpen(true);
    }
  }, [location.search]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Home
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Quick access to the main sections.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {quickLinks.map((link) => (
          <Grid key={link.id} item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea
                onClick={() => navigate(link.to)}
                sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
              >
                <CardContent sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                    <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                      {link.icon}
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                      {link.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {link.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>

            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Explore QuickField functionality
            </Typography>
          </Box>
          <Card variant="outlined">
            <CardActionArea
              onClick={() => {
                setTourSlide(0);
                setTourOpen(true);
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <HelpOutline />
                  </Box>

                  <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                    Take a tour
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Quick guide for start
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>

          <Dialog
            open={tourOpen}
            onClose={(_event, reason) => {
              if (reason === 'escapeKeyDown' && lightbox) {
                return;
              }
              setTourOpen(false);
              setLightbox(null);
            }}
            maxWidth="md"
            fullWidth
            onKeyDown={(e) => {
              if (lightbox) {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  closeLightbox();
                }
                return;
              }
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handlePrev();
              }
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                handleNext();
              }
            }}
            TransitionProps={{
              onEntered: () => dialogPaperRef.current?.focus(),
            }}
            PaperProps={{
              ref: dialogPaperRef,
              tabIndex: -1,
            }}
          >
            <DialogTitle sx={{ pb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="div" sx={{ flex: 1 }}>
                  QuickField tour
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 56, textAlign: 'center' }}
                >
                  {tourSlide + 1} / {slides.length}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                position: 'relative',
                height: { xs: '72vh', md: 640 },
                overflowY: 'auto',
                px: { xs: 2, sm: 7 },
              }}
            >
              <IconButton
                aria-label="Previous"
                onClick={handlePrev}
                disabled={tourSlide === 0}
                color="primary"
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                    boxShadow: 'none',
                  },
                }}
              >
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="Next"
                onClick={handleNext}
                disabled={tourSlide === slides.length - 1}
                color="primary"
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                    boxShadow: 'none',
                  },
                }}
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>



              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {activeSlide.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: activeSlide.steps ? 1.25 : 2 }}>
                {activeSlide.body}
              </Typography>

              {activeSlide.steps && (
                <Box component="ol" sx={{ pl: 3, mt: 0, mb: 2 }}>
                  {activeSlide.steps.map((step: string, idx: number) => (
                    <Typography key={idx} component="li" variant="body2" sx={{ mb: idx === activeSlide.steps.length - 1 ? 0 : 0.75 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    activeSlide.images.length === 1 ? '1fr' : activeSlide.images.length === 2 ? '1fr 1fr' : '1fr 1fr 1fr',
                  gap: 1.5,
                }}
              >
                {activeSlide.images.map((img: { src: string; alt: string }) => (
                  <Box
                    key={img.src}
                    component="img"
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    onClick={() => setLightbox({ src: img.src, alt: img.alt })}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: 'background.paper',
                      cursor: 'zoom-in',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                  />
                ))}
              </Box>

              {lightbox && (
                <Box
                  role="presentation"
                  onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                      closeLightbox();
                    }
                  }}
                  sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: (t) => t.zIndex.modal + 2,
                    bgcolor: 'rgba(0,0,0,0.72)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={lightbox.src}
                    alt={lightbox.alt}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      maxWidth: 'min(1100px, 96vw)',
                      maxHeight: 'min(86vh, 900px)',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: 1,
                      boxShadow: 8,
                      cursor: 'default',
                      userSelect: 'none',
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setLightbox(null);
                  setTourOpen(false);
                }}
                variant="contained"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    </Container>
  );
};
